import React, { useState, useEffect, useMemo } from "react";
import { GetPriceList } from "../../../server/PriceList";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { formatExpirationDate } from "../../../utils/formatExpirationDate";
import { readHasExpirationDate } from "../../../utils/productExpirationDisplay";
import { findPackagePromo } from "../sales/promotionUtils";
import {
  getActivePromo,
  getPromoCompareBadgeParts,
  getPromoSalePrice,
  promoShowsStrikePrice,
} from "./priceMarginUtils";

const isProduct = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

const PricesCompareTable = ({
  selectedProd,
  mobileAction = null,
  showExpiration = true,
}) => {
  const [company, setCompany] = useState([]);
  const { pathname } = useLocation();

  const role = useSelector((state) => state.user.role);

  useEffect(() => {
    let cancelled = false;

    const parseRows = (data) => {
      return Object.entries(data).flatMap(([retailer, retailerData]) => {
        const branches = retailerData?.branches;
        if (!branches || typeof branches !== "object") return [];

        return Object.entries(branches).flatMap(([branch, branchData]) =>
          (branchData?.prices || []).map((price) => ({
            retailer,
            branch,
            retailerName: branchData?.retailerName || retailer,
            branchName: branchData?.branchName,
            price: price.price,
            date: price.date,
            promotionDescription: price.promotionDescription,
          })),
        );
      });
    };

    (async () => {
      try {
        const gtin = selectedProd?.gtin;
        if (gtin == null || String(gtin).trim() === "") {
          if (!cancelled) setCompany([]);
          return;
        }

        const data = await GetPriceList(gtin, role);
        if (cancelled) return;
        if (!data || typeof data !== "object") {
          setCompany([]);
          return;
        }

        let formattedData = parseRows(data);

        const shaareiRevachaEntries = formattedData.filter((entry) =>
          entry.retailerName?.includes("שערי רווחה"),
        );

        if (
          shaareiRevachaEntries.length === 2 &&
          shaareiRevachaEntries[0].price === shaareiRevachaEntries[1].price
        ) {
          shaareiRevachaEntries[0].branchName = null;
          formattedData = formattedData.filter(
            (entry) => entry !== shaareiRevachaEntries[1],
          );
        }

        if (cancelled) return;
        setCompany(
          formattedData.sort((a, b) => {
            const pa = parseFloat(a.price);
            const pb = parseFloat(b.price);
            return (
              (Number.isFinite(pa) ? pa : 0) - (Number.isFinite(pb) ? pb : 0)
            );
          }),
        );
      } catch {
        if (!cancelled) setCompany([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedProd, role]);

  const { onlineStores, physicalStores } = useMemo(() => {
    const online = [];
    const physical = [];

    company.forEach((store) => {
      if (store.retailerName?.includes("המזון")) return;

      const name = store.branchName
        ? `${store.retailerName} - ${store.branchName}`
        : store.retailerName;

      const hasStorePromo =
        store.promotionDescription &&
        /^\d+(\.\d{1,2})? ₪$/.test(store.promotionDescription);

      const entry = {
        name,
        price: parseFloat(store.price).toFixed(2),
        hasStorePromo,
        promotionDescription: store.promotionDescription || null,
      };

      if (name.includes("אונליין")) {
        online.push(entry);
      } else {
        physical.push(entry);
      }
    });

    return { onlineStores: online, physicalStores: physical };
  }, [company]);

  if (!isProduct(selectedProd)) {
    return null;
  }

  const hasExpirationDate = readHasExpirationDate(selectedProd);
  const expirationLabel =
    hasExpirationDate &&
    formatExpirationDate(selectedProd.availability?.expiration);

  const promo = findPackagePromo(selectedProd) ?? getActivePromo(selectedProd);
  const originalPrice =
    selectedProd.pricing?.price != null
      ? parseFloat(selectedProd.pricing.price)
      : null;
  const promoBadge = getPromoCompareBadgeParts(promo, originalPrice);
  // Caption promos (qty/threshold/package): keep regular list price + tag.
  // Package tag carries only the package unit sale (hero is the single-unit price).
  const salePrice = promoBadge ? null : getPromoSalePrice(originalPrice, promo);
  const hamazonPrice =
    salePrice != null
      ? salePrice.toFixed(2)
      : originalPrice != null
        ? originalPrice.toFixed(2)
        : null;
  const showHamazonStrike =
    salePrice != null && originalPrice != null && promoShowsStrikePrice(promo);
  const showSalePriceStyle = salePrice != null;

  const renderStorePrice = (store) => (
    <div className="price-row__price">
      {(store.hasStorePromo || !store.promotionDescription) && (
        <span
          className={`price-row__amount${
            store.hasStorePromo ? " price-row__original" : ""
          }`}
        >
          <span>₪</span>
          {store.price}
        </span>
      )}
      {store.promotionDescription && (
        <span
          className={`store-promo-desc${
            store.hasStorePromo ? " store-promo-desc--price" : ""
          }`}
        >
          {store.hasStorePromo ? (
            <>
              <span>₪</span>
              {store.promotionDescription.replace(/\s*₪\s*$/, "")}
            </>
          ) : (
            store.promotionDescription
          )}
        </span>
      )}
    </div>
  );

  const renderStoreList = (stores) =>
    stores.map((s) => (
      <div className="price-row" key={s.name}>
        <span className="price-row__name">{s.name}</span>
        {renderStorePrice(s)}
      </div>
    ));

  if (
    pathname.startsWith("/edit-product") &&
    company.length === 0 &&
    !expirationLabel
  ) {
    return null;
  }

  const hasCompetitorPrices =
    onlineStores.length > 0 || physicalStores.length > 0;

  return (
    <div className="price-comparison">
      {showExpiration && expirationLabel && (
        <p className="price-comparison__expiration">תוקף: {expirationLabel}</p>
      )}
      <div className="price-hero">
        <span className="price-hero__label">המזון — המחיר שלנו</span>
        <div className="price-hero__row">
          {showHamazonStrike && originalPrice != null && (
            <span className="price-hero__original">
              ₪{originalPrice.toFixed(2)}
            </span>
          )}
          <div
            className={`price-hero__price${
              showSalePriceStyle ? " price-hero__price--sale" : ""
            }${hamazonPrice == null ? " price-hero__price--unset" : ""}`}
          >
            {hamazonPrice != null ? (
              <>
                <span>₪</span>
                {hamazonPrice}
              </>
            ) : (
              "—"
            )}
          </div>
          {promoBadge && (
            <span className="promo-badge">
              <span>{promoBadge.label}</span>
              {promoBadge.note && (
                <span className="promo-badge__note">{promoBadge.note}</span>
              )}
            </span>
          )}
        </div>
      </div>

      {mobileAction}

      {hasCompetitorPrices && (
        <div className="price-cols">
          {onlineStores.length > 0 && (
            <div className="price-col">
              <p className="price-col-label">רשתות אונליין</p>
              <div className="price-card price-card--online">
                {renderStoreList(onlineStores)}
              </div>
            </div>
          )}
          {physicalStores.length > 0 && (
            <div className="price-col">
              <p className="price-col-label">חנויות</p>
              <div className="price-card price-card--physical">
                {renderStoreList(physicalStores)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PricesCompareTable;
